import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { ClockCounterClockwise, Package, Trash, UsersThree, X } from "@phosphor-icons/react";

const ICONS = {
  item_created: { icon: Package, tint: "bg-emerald-100 text-emerald-700" },
  item_deleted: { icon: Trash, tint: "bg-red-100 text-red-700" },
  contractor_created: { icon: UsersThree, tint: "bg-blue-100 text-blue-700" },
  contractor_deleted: { icon: Trash, tint: "bg-red-100 text-red-700" },
  default: { icon: ClockCounterClockwise, tint: "bg-zinc-100 text-zinc-600" },
};

function pickIcon(field) {
  return ICONS[field] || ICONS.default;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 45) return "just now";
  if (s < 90) return "a minute ago";
  const m = Math.floor(s / 60);
  if (m < 45) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function ProjectActivityPanel({ projectId, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/activity`, { params: { limit: 200 } });
      setRows(data);
    } catch {
      // non-fatal — panel just stays empty
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="activity-panel-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right"
        data-testid="project-activity-panel"
        style={{ animation: "slideInRight 0.25s ease-out" }}
      >
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-zinc-400 flex items-center gap-1.5">
              <ClockCounterClockwise size={11} weight="bold" /> Project activity
            </p>
            <h2 className="font-display text-lg font-semibold tracking-tight text-[#001E44]">
              What's been happening
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-[#001E44] transition-colors"
            data-testid="close-activity-panel"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-zinc-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400">
              No activity yet — start by adding items or team members.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {rows.map((r) => {
                const { icon: Icon, tint } = pickIcon(r.field);
                return (
                  <li key={r.id} className="px-5 py-3.5 flex items-start gap-3" data-testid={`activity-row-${r.id}`}>
                    <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${tint}`}>
                      <Icon size={14} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#001E44] leading-snug">
                        <span className="font-semibold">{r.user_name || "Someone"}</span>{" "}
                        <span className="text-zinc-600">{r.text}</span>
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5" title={r.created_at}>
                        {timeAgo(r.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0.5; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
